<template>
  <div>
    <div
      v-if="!hideAdjustmentAction"
      class="mb-3"
    >
      <CButton
        color="primary"
        class="mr-3"
        @click="toggleAllowSelectionForDisputeAdjustments"
      >
        {{ allowRowSelection ? 'Disable Selection' : 'Select Calls for Dispute Adjustments' }}
      </CButton>
      <CButton
        v-if="allowRowSelection"
        color="success"
        :disabled="noCallsSelected"
        @click="goToSetNewPaymentForCalls"
      >
        Set New Payment for Calls
      </CButton>
    </div>

    <CDataTable
      small
      border
      sorter
      :loading="loading"
      column-filter
      :items="callsFormatted"
      :fields="fields"
      :items-per-page="15"
      :sorter-value="{ column: 'callDate', asc: false }"
      :pagination="{ align: 'center' }"
      :no-items-view="{ noResults: 'No calls found', noItems: 'No calls, search with different filters' }"
      :clickable-rows="allowRowSelection"
      @row-clicked="handleCallSelection"
    >
      <template #durationInSeconds="{item}">
        <td class="text-right">
          {{ item.duration }}
        </td>
      </template>

      <template #connectedDurationInSeconds="{item}">
        <td class="text-right">
          {{ item.connectedDuration }}
        </td>
      </template>

      <template #selectCall="{item}">
        <td class="text-right">
          <input
            v-model="callsSelectedMap[item.callId]"
            type="checkbox"
          >
        </td>
      </template>

      <template #callDate="{item}">
        <td>
          {{ item.callDateFormatted }}
        </td>
      </template>

      <template #callId="{item}">
        <td class="text-right">
          <div class="d-flex align-items-center">
            <div
              class="text-truncate w-fixed-70 mr-2"
            >
              {{ item.callId }}
            </div>

            <a
              href
              @click.prevent="copyCallIdToClipboard(item.callId)"
            ><CIcon
              name="cil-copy"
            /></a>
          </div>
        </td>
      </template>

      <template #callerId="{item}">
        <td class="text-right">
          <div class="d-flex align-items-center">
            {{ item.callerId }}

            <a
              href
              class="ml-1"
              @click.prevent="copyCallerIdToClipboard(item.callerId)"
            ><CIcon
              name="cil-copy"
            /></a>
          </div>
        </td>
      </template>

      <template #conversionAmount="{item}">
        <td class="text-right">
          {{ formatAmount(item.conversionAmount) }}
        </td>
      </template>

      <template #payoutAmount="{item}">
        <td class="text-right">
          {{ formatAmount(item.payoutAmount) }}
        </td>
      </template>

      <template #disputedConversionAdjustmentAmount="{item}">
        <td class="text-right">
          {{ formatAmount(item.disputedConversionAdjustmentAmount) }}
        </td>
      </template>

      <template #disputedPayoutAdjustmentAmount="{item}">
        <td class="text-right">
          {{ formatAmount(item.disputedPayoutAdjustmentAmount) }}
        </td>
      </template>

      <template #sellerDisputeFees="{item}">
        <td class="text-right">
          {{ formatAmount(item.sellerDisputeFees) }}
        </td>
      </template>

      <template #buyerDisputeFees="{item}">
        <td class="text-right">
          {{ formatAmount(item.buyerDisputeFees) }}
        </td>
      </template>

      <template #sellerAccountName="{ item }">
        <td>
          <router-link
            v-if="item.sellerAccountId"
            :to="{ name: 'SellerAccountDetails', params: { id: item.sellerAccountId} }"
            target="_blank"
          >
            <strong>{{ item.sellerAccountName }}</strong>

            <CIcon
              name="cil-external-link"
              size="custom"
              class="ml-1"
            />
          </router-link>
        </td>
      </template>

      <template #buyerAccountName="{ item }">
        <td>
          <router-link
            v-if="item.buyerAccountId"
            :to="{ name: 'BuyerAccountDetails', params: { id: item.buyerAccountId} }"
            target="_blank"
          >
            <strong>{{ item.buyerAccountName }}</strong>

            <CIcon
              name="cil-external-link"
              size="custom"
              class="ml-1"
            />
          </router-link>
        </td>
      </template>

      <template #buyerCampaignName="{ item }">
        <td>
          <router-link
            v-if="item.buyerCampaignId"
            :to="{ name: 'BuyerCampaignDetails', params: { id: item.buyerCampaignId } }"
            target="_blank"
          >
            <strong>{{ item.buyerCampaignName }}</strong>

            <CIcon
              name="cil-external-link"
              size="custom"
              class="ml-1"
            />
          </router-link>
        </td>
      </template>

      <template #sellerOfferName="{ item }">
        <td>
          <router-link
            v-if="item.sellerOfferId"
            :to="{ name: 'SellerOfferEdit', params: { id: item.sellerOfferId } }"
            target="_blank"
          >
            <strong>{{ item.sellerOfferName }}</strong>

            <CIcon
              name="cil-external-link"
              size="custom"
              class="ml-1"
            />
          </router-link>
        </td>
      </template>

      <template #creativeId="{item}">
        <td class="text-right">
          {{ getSafe(() => item.creativeId) }}
        </td>
      </template>

      <template #cmsId="{item}">
        <td class="text-right">
          {{ getSafe(() => item.cmsId) }}
        </td>
      </template>

      <template #Actions="{item}">
        <td>
          <slot
            name="actions"
            :call="item"
          >
            <CButton
              v-if="item.recordingUrl"
              color="info"
              size="sm"
              class="mt-1 mr-2"
              :href="item.recordingUrl"
              target="_blank"
            >
              Recording
            </CButton>

            <CButton
              color="info"
              size="sm"
              class="mt-1"
              @click="setChannelToBlockFromCall(item)"
            >
              Block Channel
            </CButton>
          </slot>
        </td>
      </template>
    </CDataTable>

    <CModal      
      v-if="callToBlockChannel"
      title="Confirm channel block"
      color="warning"
      :show.sync="showBlockChannelModal"
    >
      <p>
        Are you sure you want to block <strong><u>{{ callToBlockChannel.channel }}</u></strong> channel with seller as <strong><u>{{ callToBlockChannel.sellerAccountName }}</u></strong> for buyer <strong><u>{{ callToBlockChannel.buyerAccountName }}</u></strong>?
      </p>

      <template #footer>
        <CButton
          color="light"
          @click="showBlockChannelModal = false"
        >
          Cancel
        </CButton>

        <ButtonLoading
          color="danger"
          :loading="isLoadingBlockChannel"
          @click="confirmBlockChannel"
        >
          Block
        </ButtonLoading>        
      </template>
    </CModal>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import startCase from 'lodash.startcase'

import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { 
  formatDateTime,
  formatDate,
  formatAmount,
  convertDurationInSecsToTimeFormat,
} from '@/utils'
import { showSuccessMessage } from '@/notification-utils'

const columnFields = [
  { key: 'selectCall', label: '', filter: false, sorter: false,  },
  { key: 'callDate', label: 'Call Date (in local time)', _classes: 'w-fixed-200'  },
  { key: 'callState', label: 'State', sorter: false,  },
  { key: 'categoryName', label: 'Category', },
  { key: 'callId', label: 'Call Id', sorter: false  },
  { key: 'callerId', label: 'Caller Id', sorter: false  },
  { key: 'sellerAccountName', label: 'Seller', },
  { key: 'buyerAccountName', label: 'Buyer', },
  { key: 'buyerCampaignName', label: 'Buyer Campaign', },
  { key: 'channel', label: 'Channel', },
  { key: 'trafficSource', label: 'Traffic Source', },
  { key: 'language', _classes: 'w-fixed-70' },
  { key: 'country', _classes: 'w-fixed-70' },
  { key: 'durationInSeconds', label: 'Duration', _classes: 'text-right' },
  { key: 'connectedDurationInSeconds', label: 'Connected Duration', _classes: 'text-right' },
  { key: 'creativeId', label: 'Creative Id',  },
  { key: 'cmsId', label: 'SMID (CMS Id)', },
  { key: 'conversionAmount', label: 'Conversion Amount', _classes: 'text-right' },
  { key: 'disputedConversionAdjustmentAmount', label: 'Disputed Conversion Amount',  },
  { key: 'payoutAmount', label: 'Payout Amount', _classes: 'text-right' },
  { key: 'disputedPayoutAdjustmentAmount', label: 'Disputed Payout Amount',  },
  { key: 'sellerDisputeFees', label: 'Seller Dispute Fees', _classes: 'text-right' },
  { key: 'buyerDisputeFees', label: 'Buyer Dispute Fees', _classes: 'text-right' },
  { key: 'sellerOfferName', label: 'Seller Offer',  },
  { key: 'disputeReasonByClient', label: 'Reason For Dispute',  },
  { key: 'disputeCommentsByAdmin', label: 'Admin Review Comments',  },
  { key: 'Actions', filter: false, sorter: false },
]

export default {
  name: 'CallsList',

  mixins: [copyToClipboardMixin],

  props: {
    calls: {
      type: Array,
      default() {
        return []
      }
    },

    loading: {
      type: Boolean,
      default: false
    },

    hideAdjustmentAction: {
      type: Boolean,
      default: false
    },

    dateFrom: {
      type: String,
      default: ''
    },

    dateTo: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      showBlockChannelModal: false,
      salesChannelName: '',
      callToBlockChannel: undefined,
      isLoadingBlockChannel: false,
      allowRowSelection: false,
      callsSelectedMap: {},
    }
  },

  computed: {
    fields() {
      const fields = [...columnFields]

      if (!this.allowRowSelection) {
        fields.splice(0, 1)
      }

      return fields
    },

    callsFormatted() {
      return this.calls.map(call => {
        return {
          ...call,
          sellerAccountName: call.sellerAccountName ?? '',
          buyerAccountName: call.buyerAccountName ?? '',
          channel: call.channel ?? '',
          country: call.country ?? '',
          sellerOfferName: call.sellerOfferName ?? '',
          callDateFormatted: formatDateTime(call.callDateTimeOffset),
          duration: convertDurationInSecsToTimeFormat(call.durationInSeconds),
          connectedDuration: convertDurationInSecsToTimeFormat(call.connectedDurationInSeconds),
          trafficSource: call.trafficSourceName ? `${call.trafficSourceType}-${call.trafficSourceName}` : '',
          callState: startCase(call.callStateId),
          disputedConversionAdjustmentAmount: call.disputedConversionAdjustmentAmount ?? '',
          disputedPayoutAdjustmentAmount: call.disputedPayoutAdjustmentAmount ?? '',
          sellerDisputeFees: call.sellerDisputeFees ?? '',
          buyerDisputeFees: call.buyerDisputeFees ?? '',
          disputeReasonByClient: call.disputeReasonByClient ?? '',
          disputeCommentsByAdmin: call.disputeCommentsByAdmin ?? '',
        }
      })
    },

    noCallsSelected() {
      return !Object.keys(this.callsSelectedMap).length
    }
  },

  methods: {
    ...mapActions('salesChannel', ['block']),

    setChannelToBlockFromCall(call) {
      this.callToBlockChannel = call
      this.showBlockChannelModal = true
    },

    async confirmBlockChannel() {
      try {
        this.isLoadingBlockChannel = true

        let request = {
          language: this.callToBlockChannel.language,
          country: this.callToBlockChannel.country,
          trafficSourceId: this.callToBlockChannel.trafficSourceId,
          categoryId: this.callToBlockChannel.categoryId,
          buyerAccountId: this.callToBlockChannel.buyerAccountId,
          sellerAccountId: this.callToBlockChannel.sellerAccountId
        }

        await this.block({ payload: request })

        showSuccessMessage('Successfully blocked channel')
      } catch {
        //
      } finally {
        this.isLoadingBlockChannel = false
        this.showBlockChannelModal = false
      }
    },

    formatDateTime,

    formatDate,

    formatAmount,

    copyCallIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Call Id copied to clipboard')
    },

    copyCallerIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Caller Id copied to clipboard')
    },

    toggleAllowSelectionForDisputeAdjustments() {
      this.allowRowSelection = !this.allowRowSelection

      if (!this.allowRowSelection) {
        this.callsSelectedMap = {}
      }
    },

    handleCallSelection(call) {
      if (!this.allowRowSelection) {
        return
      }

      if (this.callsSelectedMap[call.callId]) {
        delete this.callsSelectedMap[call.callId]
      } else {
        this.$set(this.callsSelectedMap, call.callId, true)
      }

    },

    goToSetNewPaymentForCalls() {
      this.$router.push({
        name: 'DisputesManualAdjustments',
        query: {
          callids: Object.keys(this.callsSelectedMap).join(','),
          daterange: `${this.dateFrom},${this.dateTo}`
        }
      })
    }
  },
}
</script>
<style lang="">
  
</style>